import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from './ThemeContext';

const SEOContext = createContext();

export const useSEO = () => {
  const context = useContext(SEOContext);
  if (!context) {
    throw new Error('useSEO must be used within a SEOProvider');
  }
  return context;
};

export const SEOProvider = ({ children }) => {
  const { domainConfig } = useTheme();
  const location = useLocation();

  // Get current page name from path
  const getCurrentPage = (pathname) => {
    const path = pathname || location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/solutions')) return 'solutions';
    if (path.startsWith('/products')) return 'products';
    if (path.startsWith('/resources')) return 'resources';
    if (path.startsWith('/blog')) return 'blog';
    if (path.startsWith('/contact')) return 'contact';
    if (path.startsWith('/request-demo')) return 'request-demo';
    if (path.startsWith('/privacy')) return 'privacy';
    if (path.startsWith('/compare-prices')) return 'compare-prices';
    return 'home';
  };

  // Get production origin from domain config (not window.location.origin which would be localhost during pre-render)
  const getProductionOrigin = () => {
    // Use ogUrl from SEO config
    if (domainConfig?.seo?.ogUrl) {
      try {
        return new URL(domainConfig.seo.ogUrl).origin;
      } catch (e) { /* fall through */ }
    }
    // Fallback to empty string (will use relative URLs)
    return '';
  };

  // Helper to get current URL safely using production origin
  const getCurrentUrl = () => {
    const origin = getProductionOrigin();
    return `${origin}${location.pathname}`;
  };

  const getCurrentOrigin = () => {
    return getProductionOrigin();
  };

  // Generate SEO data for current page - SYNCHRONOUSLY with useMemo
  // This ensures SEO is available on first render for react-snap pre-rendering
  const currentSEO = useMemo(() => {
    if (!domainConfig?.seo) {
      return {
        title: domainConfig?.name || 'Fleet Management Solutions',
        description: domainConfig?.description || 'Advanced fleet management solutions',
        keywords: ['fleet management', 'GPS tracking', 'fleet solutions'],
        image: domainConfig?.logo || '/assets/logos/default-logo.png',
        url: getCurrentUrl(),
        type: 'website'
      };
    }

    const currentPage = getCurrentPage(location.pathname);
    const baseSEO = domainConfig.seo;
    const pageSEO = baseSEO.pages?.[currentPage] || {};

    // Combine base SEO with page-specific SEO
    const seoData = {
      title: pageSEO.title || baseSEO.title || domainConfig.name,
      description: pageSEO.description || baseSEO.description || domainConfig.description,
      keywords: pageSEO.keywords || baseSEO.keywords || [],
      image: baseSEO.ogImage || domainConfig.logo || '/assets/logos/default-logo.png',
      url: getCurrentUrl(),
      type: 'website',
      
      // Open Graph data
      ogTitle: pageSEO.ogTitle || baseSEO.ogTitle || pageSEO.title || baseSEO.title,
      ogDescription: pageSEO.ogDescription || baseSEO.ogDescription || pageSEO.description || baseSEO.description,
      ogType: baseSEO.ogType || 'website',
      ogUrl: baseSEO.ogUrl || getCurrentUrl(),
      ogImage: baseSEO.ogImage || domainConfig.logo,
      
      // Twitter Card data
      twitterCard: baseSEO.twitterCard || 'summary_large_image',
      twitterTitle: pageSEO.twitterTitle || baseSEO.twitterTitle || pageSEO.title || baseSEO.title,
      twitterDescription: pageSEO.twitterDescription || baseSEO.twitterDescription || pageSEO.description || baseSEO.description,
      twitterImage: baseSEO.twitterImage || baseSEO.ogImage || domainConfig.logo,
      
      // Robots
      robots: pageSEO.robots || baseSEO.robots || 'index, follow',
      
      // Author and publisher
      author: baseSEO.author || domainConfig.name,
      publisher: baseSEO.publisher || domainConfig.name,
      
      // Page-specific data
      h1: pageSEO.h1,
      h2: pageSEO.h2,
      
      // Technical SEO
      technical: baseSEO.technical || {}
    };

    return seoData;
  }, [location.pathname, domainConfig]); // Recalculate when path or config changes

  // Generate structured data
  const generateStructuredData = () => {
    if (!domainConfig) return null;

    const origin = getCurrentOrigin();

    // Get localBusiness config from domain config's SEO section
    const localBusiness = domainConfig?.seo?.localBusiness || {};

    const baseStructuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": domainConfig.name,
      "description": domainConfig.description,
      "url": origin,
      "logo": {
        "@type": "ImageObject",
        "url": `${origin}${domainConfig.logo}.png` // Always use .png extension
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": localBusiness.streetAddress || "",
        "addressLocality": localBusiness.addressLocality || "",
        "addressRegion": localBusiness.addressRegion || "",
        "postalCode": localBusiness.postalCode || "",
        "addressCountry": localBusiness.addressCountry || "US"
      }
    };

    // Only add contactPoint if telephone is provided
    if (localBusiness.telephone) {
      baseStructuredData.contactPoint = {
        "@type": "ContactPoint",
        "telephone": localBusiness.telephone,
        "contactType": "customer service",
        "availableLanguage": "English"
      };
    }

    const currentPage = getCurrentPage();
    
    // Add WebSite structured data for homepage
    // Note: WebSite schema doesn't support "logo" property, so we create a separate object
    if (currentPage === 'home') {
      return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": domainConfig.name,
        "description": domainConfig.description,
        "url": origin,
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${origin}/search?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      };
    }

    // Add Article structured data for blog posts
    // Note: Article schema has specific properties - don't spread Organization properties
    if (currentPage === 'blog' && location.pathname !== '/blog') {
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": currentSEO?.title,
        "description": currentSEO?.description,
        "image": currentSEO?.image ? (currentSEO.image.startsWith('http') ? currentSEO.image : `${origin}${currentSEO.image}`) : `${origin}${domainConfig.logo}`,
        "author": {
          "@type": "Person",
          "name": currentSEO?.author || domainConfig.name
        },
        "publisher": {
          "@type": "Organization",
          "name": domainConfig.name,
          "logo": {
            "@type": "ImageObject",
            "url": `${origin}${domainConfig.logo}.png` // Always use .png extension
          }
        },
        "datePublished": new Date().toISOString(),
        "dateModified": new Date().toISOString(),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${origin}${location.pathname}`
        }
      };
    }

    return baseStructuredData;
  };

  // Generate breadcrumb structured data
  const generateBreadcrumbStructuredData = () => {
    const origin = getCurrentOrigin();
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const breadcrumbs = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": origin
      }
    ];

    let currentPath = origin;
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      breadcrumbs.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": segment.charAt(0).toUpperCase() + segment.slice(1),
        "item": currentPath
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs
    };
  };

  // Generate product structured data
  const generateProductStructuredData = (product) => {
    if (!product || !domainConfig) return null;

    const baseUrl = getCurrentOrigin();

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name || product.title,
      "description": product.description,
      "brand": {
        "@type": "Brand",
        "name": product.brand || domainConfig.name
      }
    };

    // Build proper image URL
    if (product.image) {
      structuredData.image = product.image.startsWith('http')
        ? product.image
        : `${baseUrl}/assets/images/${product.image}.webp`;
    } else if (domainConfig.logo) {
      structuredData.image = `${baseUrl}${domainConfig.logo}.png`; // Always use .png extension
    }

    // Add SKU if provided
    if (product.sku || product.id) {
      structuredData.sku = product.sku || `PROD-${product.id}`;
    }

    // Add category if provided
    if (product.category) {
      structuredData.category = product.category;
    }

    // Add URL if provided or construct from product id
    if (product.url) {
      structuredData.url = product.url.startsWith('http') ? product.url : `${baseUrl}${product.url}`;
    } else if (product.id) {
      structuredData.url = `${baseUrl}/products/${product.id}`;
    }

    // Only add offers if price is provided
    if (product.price) {
      structuredData.offers = {
        "@type": "Offer",
        "price": product.price,
        "priceCurrency": product.currency || "USD",
        "availability": product.availability || "https://schema.org/InStock",
        "itemCondition": product.condition || "https://schema.org/NewCondition",
        "seller": {
          "@type": "Organization",
          "name": domainConfig.name
        }
      };

      // Add offer URL if available
      if (structuredData.url) {
        structuredData.offers.url = structuredData.url;
      }
    }

    // Add aggregate rating if provided
    if (product.rating || product.aggregateRating) {
      const rating = product.aggregateRating || product.rating;
      structuredData.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": rating.value || rating.ratingValue || rating,
        "bestRating": rating.bestRating || "5",
        "reviewCount": rating.count || rating.reviewCount || "1"
      };
    }

    return structuredData;
  };

  // Generate FAQ structured data
  const generateFAQStructuredData = (faqs) => {
    if (!faqs || !Array.isArray(faqs) || faqs.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  };

  const value = {
    currentSEO,
    generateStructuredData,
    generateBreadcrumbStructuredData,
    generateProductStructuredData,
    generateFAQStructuredData,
    getCurrentPage
  };

  return (
    <SEOContext.Provider value={value}>
      {children}
    </SEOContext.Provider>
  );
};
